import React, { useState, useEffect } from "react";
import MasterLayout from "../masterLayout/MasterLayout";
import Breadcrumb from "../components/Breadcrumb";
import StaffTable from "../components/StaffTable";
import { API_URL, LOCATION_ID, TOKEN } from "..";
import { Spinner } from "react-bootstrap";
import { Icon } from '@iconify/react/dist/iconify.js';
import Swal from 'sweetalert2';

const StaffPage = () => {

    const TOKEN = localStorage.getItem('tokenID');
    const currentUser = JSON.parse(localStorage.getItem('user') || 'null');
    const isAdmin = currentUser?.roles?.role === 'admin' || currentUser?.role === 'admin';

    const [data, setData] = useState({ admins: [], users: [] });
    const [loading, setLoading] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState("admins");

    const fetchUsers = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(
                `https://services.leadconnectorhq.com/users/?locationId=${LOCATION_ID}`,
                {
                    method: "GET",
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${TOKEN}`,
                        Version: "2021-07-28"
                    },
                }
            );
            const dataFor = await response.json();

            // Fetch local users to merge custom phone/designation/city
            let localUsersList = [];
            try {
                const localRes = await fetch(`${API_URL}/api/user/fetch`);
                const localData = await localRes.json();
                if (localData && localData.data) {
                    localUsersList = localData.data;
                }
            } catch (e) {
                console.error("Error fetching local users:", e);
            }

            setLoading(false);
            if (response.status === 200 && dataFor.users) {
                const mapStaff = (user) => {
                    const localU = localUsersList.find(lu => lu.crm_id === user.id || String(lu.id) === String(user.id)) || {};
                    const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.name || localU.name || 'N/A';
                    const email = user.email || localU.email || 'N/A';
                    const phone = user.phone || localU.phone || 'N/A';
                    return {
                        id: user.id || localU.crm_id || localU.id || 'N/A',
                        name: fullName,
                        email: email,
                        phone: phone,
                        job_description: localU.job_description || 'N/A',
                        role: user?.roles ? user.roles.role : (user.role || localU.role || 'N/A'),
                        cityId: localU.cityId || localU.city?.id || null,
                        city: localU.city || null,
                        cityName: localU.city?.name || 'N/A'
                    };
                };

                const admins = dataFor.users
                    .filter(user => user?.roles?.role?.toLowerCase() === "admin")
                    .map(mapStaff);

                const users = dataFor.users
                    .filter(user => user?.roles?.role?.toLowerCase() !== "admin")
                    .map(mapStaff);

                setData({ admins, users });
            } else {
                throw new Error(dataFor.message || 'Failed to fetch users');
            }
        } catch (err) {
            setLoading(false);
            setError(err.message || 'Error fetching users from GoHighLevel');
        }
    };

    const handleSyncStaff = async () => {
        try {
            setSyncing(true);
            // 1. Fetch fresh staff users from GoHighLevel
            const ghlRes = await fetch(
                `https://services.leadconnectorhq.com/users/?locationId=${LOCATION_ID}`,
                {
                    method: "GET",
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${TOKEN}`,
                        Version: "2021-07-28"
                    },
                }
            );
            const ghlData = await ghlRes.json();
            if (!ghlRes.ok || !ghlData.users) {
                throw new Error(ghlData.message || 'Failed to fetch staff from GoHighLevel');
            }

            // 2. Call backend sync-all endpoint with GHL users
            const syncRes = await fetch(`${API_URL}/api/user/sync-all`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${TOKEN}`
                },
                body: JSON.stringify({ users: ghlData.users })
            });
            const syncResult = await syncRes.json();

            if (syncRes.ok && syncResult.success) {
                await Swal.fire({
                    icon: 'success',
                    title: 'Staff Synchronized!',
                    text: syncResult.message || 'Staff members have been synchronized with GoHighLevel.',
                    confirmButtonColor: '#111827',
                });
                await fetchUsers();
            } else {
                throw new Error(syncResult.message || syncResult.error || 'Failed to synchronize staff');
            }
        } catch (err) {
            console.error('Sync staff error:', err);
            Swal.fire({
                icon: 'error',
                title: 'Sync Failed',
                text: err.message || 'An error occurred while synchronizing staff.',
                confirmButtonColor: '#111827',
            });
        } finally {
            setSyncing(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    return (
        <MasterLayout>
            <Breadcrumb title="Staff" />

            {error && (
                <div className="alert alert-danger alert-dismissible fade show mb-4" role="alert">
                    {error}
                    <button
                        type="button"
                        className="btn-close"
                        onClick={() => setError(null)}
                        aria-label="Close"
                    ></button>
                </div>
            )}

            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <ul className="nav nav-tabs mb-0 border-bottom-0">
                    <li className="nav-item">
                        <button
                            className={`nav-link ${activeTab === "admins" ? "active" : ""}`}
                            onClick={() => setActiveTab("admins")}
                        >
                            Admins ({data.admins.length})
                        </button>
                    </li>
                    <li className="nav-item">
                        <button
                            className={`nav-link ${activeTab === "users" ? "active" : ""}`}
                            onClick={() => setActiveTab("users")}
                        >
                            Users ({data.users.length})
                        </button>
                    </li>
                </ul>

                {isAdmin && (
                    <button
                        type="button"
                        className="btn btn-primary d-flex align-items-center gap-2"
                        onClick={handleSyncStaff}
                        disabled={syncing || loading}
                        style={{
                            fontWeight: 600,
                            borderRadius: '8px',
                            padding: '8px 18px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
                        }}
                    >
                        {syncing ? (
                            <>
                                <Spinner size="sm" animation="border" />
                                <span>Syncing Staff...</span>
                            </>
                        ) : (
                            <>
                                <Icon icon="mdi:sync" style={{ fontSize: '19px' }} />
                                <span>Sync Staff</span>
                            </>
                        )}
                    </button>
                )}
            </div>

            {loading && (
                <div className="text-center mt-4">
                    <Spinner />
                    <p>Loading staff data...</p>
                </div>
            )}

            {!loading && activeTab === "admins" && data.admins.length === 0 && (
                <p>No Admins Found...</p>
            )}
            {!loading && activeTab === "users" && data.users.length === 0 && (
                <p>No Users Found...</p>
            )}

            {!loading && activeTab === "admins" && data.admins.length > 0 && (
                <StaffTable data={data.admins} showPrivileges={false} onEditSuccess={fetchUsers} />
            )}
            {!loading && activeTab === "users" && data.users.length > 0 && (
                <StaffTable data={data.users} showPrivileges={true} onEditSuccess={fetchUsers} />
            )}
        </MasterLayout>
    );
};

export default StaffPage;